import { peekBranding } from "@/lib/branding";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X, Check, Loader2, CalendarOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fmtDay = (iso) => {
  if (!iso) return "";
  try {
    return new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("en-CA", { weekday: "long", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
};

/**
 * Time-off approval dialog — opens when an admin taps a time-off
 * notification. Approve marks the days BOOKED OFF on the Hours grid; Deny
 * just records the decision. The employee is notified either way.
 */
const TimeOffRequestDialog = ({ notification, onClose, testid = "timeoff-request" }) => {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/time-off/${notification.meta.request_id}`);
        setReq(data);
      } catch {
        toast.error("Could not load the request");
        onClose();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Escape closes (full-screen overlay — keyboard users need a way out).
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolve = async (approve) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/time-off/${req.request_id}/resolve`, { approve });
      setReq(data);
      toast.success(approve ? "Approved — days marked booked off on the Hours grid" : "Request denied");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resolve the request");
    } finally { setBusy(false); }
  };

  const dates = req ? (req.date_from === req.date_to ? fmtDay(req.date_from) : `${fmtDay(req.date_from)} → ${fmtDay(req.date_to)}`) : "";

  return createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-3" data-testid={`${testid}-dialog`} onClick={onClose}>
      <div className="w-full max-w-md overflow-hidden rounded-lg border border-white/10 bg-[#0b0b0f] text-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3.5">
          <img src="/api/branding/icon/elite-logo-dark.png" alt="" className="logo-dark-only h-8 w-auto object-contain" />
          <img src="/api/branding/icon/elite-logo-light.png" alt="" className="logo-light-only h-8 w-auto object-contain" />
          <div className="min-w-0 flex-1">
            <p className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#8fa4f0]">{peekBranding()?.app_name || "Elite Mechanical"}</p>
            <p className="truncate text-sm font-semibold text-white" data-testid={`${testid}-title`}>Time-off request</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" data-testid={`${testid}-close`} className="rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-6 text-white/50"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : (
            <>
              <div className="flex items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-300">
                  <CalendarOff className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm leading-relaxed text-white/80" data-testid={`${testid}-body`}>
                    <span className="font-semibold text-white">{req.employee_name}</span> is requesting time off:
                  </p>
                  <p className="mt-1 font-mono text-xs text-white/80" data-testid={`${testid}-dates`}>{dates}</p>
                  <p className="mt-2 whitespace-pre-wrap text-sm text-white/70" data-testid={`${testid}-reason`}>{req.reason}</p>
                </div>
              </div>
              {req.status === "pending" ? (
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <button
                    onClick={() => resolve(true)} disabled={busy}
                    data-testid={`${testid}-approve`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-500 disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
                  </button>
                  <button
                    onClick={() => resolve(false)} disabled={busy}
                    data-testid={`${testid}-deny`}
                    className="inline-flex items-center justify-center gap-1.5 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-500 disabled:opacity-60"
                  >
                    <X className="h-4 w-4" /> Deny
                  </button>
                </div>
              ) : (
                <p className={`mt-4 rounded-md border px-3 py-2 text-sm ${req.status === "approved" ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-200" : "border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-200"}`} data-testid={`${testid}-resolved`}>
                  {req.status === "approved" ? `Approved — ${req.days_booked_off || 0} day(s) booked off` : "Denied"}
                  {req.resolved_by_name ? ` by ${req.resolved_by_name}` : ""}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TimeOffRequestDialog;
